// modShapes.js — the shapes of sinOsc() and its family, and their settings
// -----------------------------------------------
// Pure, so it can be tested without an AudioContext: modulators.js plays
// these loops into parameters.

/** The family, in lower case: sinOsc(...) is matched without regard to case. */
export const MOD_SHAPES = ["sinosc", "triosc", "sawosc", "pulseosc", "lfnoise", "lfstep"];
const CYCLE = 2048;               // samples in one cycle's loop
const NOISE_POINTS = 256;         // random points in a noise's loop
const NOISE_SEG = 64;             // samples between two of them

/** Is this value (from the parser) one of the family? */
export function isModulator(v) {
  return !!v && typeof v === "object" && v.type === "funcCall"
    && MOD_SHAPES.includes(String(v.name).toLowerCase());
}

/**
 * The settings of a sinOsc(...) call: { shape, rate, phase, lo, hi, width }.
 * `resolve` turns an argument that is itself a call -- rand(0, 1) -- into
 * its number, drawn now (modulators.js passes the audio engine's).
 */
export function modSpec(v, resolve = x => x) {
  const num = (x, fallback) => {
    const n = Number(typeof x === "object" && x ? resolve(x) : x);
    return Number.isFinite(n) ? n : fallback;
  };
  const shape = String(v.name).toLowerCase();
  const a = v.args || [];
  if (shape === "lfnoise" || shape === "lfstep") {
    return { shape, rate: Math.max(0.001, num(a[0], 1)), phase: 0, lo: num(a[1], -1), hi: num(a[2], 1), width: 0.5 };
  }
  return {
    shape,
    rate: Math.max(0.001, num(a[0], 1)),
    phase: ((num(a[1], 0) % 1) + 1) % 1,
    lo: num(a[2], -1),
    hi: num(a[3], 1),
    width: Math.min(0.99, Math.max(0.01, num(a[4], 0.5))),
  };
}

/** One loop of the shape, -1..1: [samples, seconds the loop lasts at rate 1]. */
export function modWave(spec, random = Math.random) {
  const { shape, width } = spec;
  if (shape === "lfnoise" || shape === "lfstep") {
    const pts = Array.from({ length: NOISE_POINTS }, () => random() * 2 - 1);
    const out = new Float32Array(NOISE_POINTS * NOISE_SEG);
    for (let i = 0; i < out.length; i++) {
      const k = Math.floor(i / NOISE_SEG), f = (i % NOISE_SEG) / NOISE_SEG;
      const a = pts[k], b = pts[(k + 1) % NOISE_POINTS];
      out[i] = shape === "lfstep" ? a : a + (b - a) * (0.5 - 0.5 * Math.cos(Math.PI * f));
    }
    return [out, NOISE_SEG];            // NOISE_SEG samples per point: one point a second at rate 1
  }
  const out = new Float32Array(CYCLE);
  for (let i = 0; i < CYCLE; i++) {
    const p = i / CYCLE;
    out[i] = shape === "sinosc" ? Math.sin(2 * Math.PI * p)
      : shape === "triosc" ? (p < 0.25 ? 4 * p : p < 0.75 ? 2 - 4 * p : 4 * p - 4)
      : shape === "sawosc" ? (p < 0.5 ? 2 * p : 2 * p - 2)
      : (p < width ? 1 : -1);
  }
  return [out, CYCLE];                  // CYCLE samples per cycle
}

